import { Box } from "@mantine/core";
import classNames from "classnames";
import isNil from "lodash/isNil";
import {
  DataTable,
  DataTableColumn,
  DataTableSortStatus,
} from "mantine-datatable";
import {
  DataTableProps,
  DataTableVerticalAlignment,
} from "mantine-datatable/dist/types";
import {
  CSSProperties,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useState,
} from "react";

import SvgChevronUp from "../Icons/ChevronUp";
import SvgUnfold from "../Icons/Unfold";
import { Loader } from "../Loader";
import NoDataMessage from "../NoDataMessage/NoDataMessage";
import Pagination from "../Pagination/Pagination";
import { emptyTableSort, TableEmptyValue } from "./constants";
import { useStyles } from "./Table.styles.ts";
import { TableRow } from "./types.ts";

type TablePagination = {
  pageCount: number;
  page: number;
  onPageChange: (page: number) => void;
};

type TableExpanded = {
  recordIds: number[] | string[];
  onRecordIdsChange: Dispatch<SetStateAction<number[]>>;
};

type TableRowExpansion = {
  allowMultiple?: boolean;
  expanded: TableExpanded;
  content: (props: {
    record: TableRow;
    recordIndex: number;
    collapse: () => void;
  }) => ReactNode;
};

export type TableProps = DataTableProps<TableRow> & {
  columns?: DataTableColumn<TableRow>[];
  rows?: TableRow[];
  defaultSort?: DataTableSortStatus;
  minWidth?: CSSProperties["minWidth"];
  className?: string;
  pagination?: TablePagination;
  loading?: boolean;
  variant?: "default" | "nested";
  mih?: number;
  rowExpansion?: TableRowExpansion;
  verticalAlignment?: DataTableVerticalAlignment;
  pinLastColumn?: boolean;
  highlightOnHover?: boolean;
  noHeader?: boolean;
  onSortChange?: (sortStatus: DataTableSortStatus) => void;
  onRowClick?: (record: TableRow, index: number) => void;
};

export default function Table({
  columns,
  rows,
  defaultSort = emptyTableSort,
  minWidth,
  className,
  pagination,
  loading = false,
  variant = "default",
  mih = 200,
  h,
  highlightOnHover = true,
  withColumnBorders = true,
  onSortChange,
  ...otherProps
}: TableProps) {
  const [sort, setSort] = useState<DataTableSortStatus>(defaultSort);

  const handleSortChange = useCallback(
    (sort: DataTableSortStatus) => {
      setSort(sort);
      onSortChange && onSortChange(sort);
    },
    [onSortChange]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      pagination && pagination.onPageChange(page);
    },
    [pagination]
  );

  const { classes } = useStyles({ minWidth, variant });

  const getDefaultColumnRender = (
    record: TableRow,
    _index: number,
    key: string
  ) => {
    return <>{isNil(record[key]) ? TableEmptyValue : record[key]}</>;
  };

  if (!loading && !rows?.length) {
    return <NoDataMessage minWidth={minWidth} h={h} />;
  }

  return (
    <Box mih={mih} h={h} w="100%" pos="relative">
      <div className={classes.tableWrapper}>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/*@ts-ignore*/}
        <DataTable<TableRow>
          mih={mih}
          borderColor="rgb(176, 182, 189)"
          rowBorderColor="rgb(176, 182, 189)"
          verticalSpacing="xs"
          horizontalSpacing="xs"
          sortIcons={{ sorted: <SvgChevronUp />, unsorted: <SvgUnfold /> }}
          records={rows}
          columns={columns}
          sortStatus={sort}
          className={classNames(classes.table, className)}
          fetching={loading}
          customLoader={<Loader />}
          defaultColumnRender={getDefaultColumnRender}
          highlightOnHover={highlightOnHover}
          scrollAreaProps={{ className: classes.scrollArea, type: "auto" }}
          withColumnBorders={withColumnBorders}
          onSortStatusChange={handleSortChange}
          {...otherProps}
        />
        {pagination && (
          <Pagination
            total={pagination.pageCount}
            value={pagination.page}
            onChange={handlePageChange}
          />
        )}
      </div>
    </Box>
  );
}
